// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Plus, Trash2, Calculator, AlertCircle, DollarSign, CreditCard, Percent, Calendar, FileText } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { createInitialFeeAutoEntry, removeAutoFinancialEntry, updateAutoFinancialEntry } from '@/utils/financialAutoPosting';

// Função para formatação de moeda brasileira
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

// Função para converter string em número (aceita vários formatos)
const parseCurrency = (value: string): number => {
  if (!value) return 0;
  
  // Remove tudo exceto números, vírgulas e pontos
  let cleanValue = value.replace(/[^\d,.]/g, '');
  
  // Se tem vírgula e ponto, assume que vírgula é separador de milhares
  if (cleanValue.includes(',') && cleanValue.includes('.')) {
    // Se o ponto vem depois da vírgula, ponto é decimal
    if (cleanValue.lastIndexOf('.') > cleanValue.lastIndexOf(',')) {
      cleanValue = cleanValue.replace(/,/g, '').replace('.', ',');
    } else {
      // Vírgula é decimal, remove pontos
      cleanValue = cleanValue.replace(/\./g, '');
    }
  }
  
  // Se só tem ponto e está nas últimas 3 posições, é decimal
  if (cleanValue.includes('.') && !cleanValue.includes(',')) {
    const dotIndex = cleanValue.lastIndexOf('.');
    if (cleanValue.length - dotIndex <= 3) {
      cleanValue = cleanValue.replace('.', ',');
    } else {
      cleanValue = cleanValue.replace(/\./g, '');
    }
  }
  
  // Converte vírgula para ponto para parseFloat
  const finalValue = cleanValue.replace(',', '.');
  return parseFloat(finalValue) || 0;
};

export interface FeeConfiguration {
  // Valor do Benefício
  benefitValue: number;
  benefitValueType: 'estimated' | 'confirmed' | 'undefined';
  benefitValueNotes: string;
  
  // Configurações de parcelamento do benefício
  benefitPaymentType: 'lump_sum' | 'installments';
  benefitInstallmentCount: number;
  benefitInstallmentFrequency: 'mensal' | 'quinzenal' | 'semanal';
  benefitFirstPaymentDate: string;
  benefitFirstPaymentDatePending: boolean;
  benefitInstallmentValue: number;
  
  // Honorários Iniciais
  hasInitialFee: boolean;
  initialFeeAmount: number;
  initialFeeType: 'fixed' | 'percentage';
  initialFeeDeductFromFuture: boolean;
  initialFeePaymentStatus: 'pending' | 'paid';
  initialFeePaymentDate: string;
  initialFeeFuturePaymentDate: string;
  initialFeeAutoEntryId?: string; // ID da entrada financeira automática criada
  initialFeeBaseValue?: number;
  
  // Honorários de Êxito (UNIFICADO - inclui BPC/LOAS)
  hasSuccessFee: boolean;
  successFeeType: 'percentage_global' | 'percentage_installment' | 'fixed_global' | 'fixed_monthly';
  successFeeValue: number;
  successFeeTrigger: 'decisao' | 'recebimento' | 'ambos';
  successFeeConditions: string;
  
  // NOVA FUNCIONALIDADE: Honorários de Serviços
  isSuccessFeeIndependent: boolean; // Indica se os honorários são para serviços (sem benefício)
  successFeeIndependentValue: number; // Valor fixo dos honorários de serviços
  successFeeIndependentInstallmentCount: number; // Número de parcelas dos honorários de serviços
  successFeeIndependentInstallmentFrequency: 'mensal' | 'bimestral' | 'trimestral' | 'semestral' | 'anual'; // Frequência das parcelas
  successFeeIndependentFirstPaymentDate: string; // Data do primeiro pagamento dos honorários de serviços
  successFeeIndependentFirstPaymentDatePending: boolean; // Se a data está pendente
  successFeeIndependentAlreadyPaid: boolean; // Se parte dos honorários de serviços já foi paga
  successFeeIndependentPaidAmount: number; // Valor já pago dos honorários de serviços
  successFeeIndependentPaidDate: string; // Data do pagamento já realizado
  
  // Configuração específica para BPC/LOAS (dentro dos Honorários de Êxito)
  isBenefitUndefined: boolean;
  undefinedBenefitFirstInstallmentsCount: number;
  undefinedBenefitFirstInstallmentValue: number;
  
  // Configuração de retroativo
  hasRetroactivePayment: boolean;
  retroactivePaymentValue: number;
  retroactivePaymentType: 'fixed' | 'percentage';
  retroactivePaymentDate: string;
  retroactivePaymentDatePending: boolean;
  
  // Configuração de cobrança
  chargeOnRetroactive: boolean;
  chargeOnInstallments: boolean;
  chargeOnBoth: boolean;
  
  // Configuração Pro Bono
  isProBono: boolean;
  
  // Configurações de Pagamento
  paymentMethod: 'a_vista' | 'parcelado';
  hasDownPayment: boolean;
  downPaymentAmount: number;
  downPaymentType: 'fixed' | 'percentage';
  installmentCount: number;
  installmentFrequency: 'mensal' | 'quinzenal' | 'semanal';
  
  // Configurações Avançadas
  latePaymentInterest: number;
  earlyPaymentDiscount: number;
  gracePeriodDays: number;
  
  // Observações
  paymentConditions: string;
  collectionNotes: string;
}

interface AdvancedFeeConfigurationProps {
  initialConfig?: Partial<FeeConfiguration>;
  onConfigChange: (config: FeeConfiguration) => void;
  onSave?: (config: FeeConfiguration) => void;
}

const AdvancedFeeConfiguration: React.FC<AdvancedFeeConfigurationProps> = ({
  initialConfig = {},
  onConfigChange,
  onSave
}) => {
  const { toast } = useToast();
  const [config, setConfig] = useState<FeeConfiguration>(() => ({
    benefitValue: 0,
    benefitValueType: 'estimated',
    benefitValueNotes: '',
    benefitPaymentType: 'lump_sum',
    benefitInstallmentCount: 1,
    benefitInstallmentFrequency: 'mensal',
    benefitFirstPaymentDate: '',
    benefitFirstPaymentDatePending: false,
    benefitInstallmentValue: 0,
    hasInitialFee: false,
    initialFeeAmount: 0,
    initialFeeType: 'fixed',
    initialFeeDeductFromFuture: false,
    initialFeePaymentStatus: 'pending',
    initialFeePaymentDate: '',
    initialFeeFuturePaymentDate: '',
    initialFeeAutoEntryId: undefined,
    hasSuccessFee: false,
    successFeeType: 'percentage_global',
    successFeeValue: 0,
    successFeeTrigger: 'recebimento',
    successFeeConditions: '',
    // NOVA FUNCIONALIDADE: Honorários de Serviços
    isSuccessFeeIndependent: false,
    successFeeIndependentValue: 0,
    successFeeIndependentInstallmentCount: 1,
    successFeeIndependentInstallmentFrequency: 'mensal',
    successFeeIndependentFirstPaymentDate: '',
    successFeeIndependentFirstPaymentDatePending: false,
    successFeeIndependentAlreadyPaid: false,
    successFeeIndependentPaidAmount: 0,
    successFeeIndependentPaidDate: '',
    isBenefitUndefined: false,
    undefinedBenefitFirstInstallmentsCount: 3,
    undefinedBenefitFirstInstallmentValue: 0,
    hasRetroactivePayment: false,
    retroactivePaymentValue: 0,
    retroactivePaymentType: 'fixed',
    retroactivePaymentDate: '',
    retroactivePaymentDatePending: false,
    chargeOnRetroactive: false,
    chargeOnInstallments: true,
    chargeOnBoth: false,
    isProBono: false,
    paymentMethod: 'a_vista',
    hasDownPayment: false,
    downPaymentAmount: 0,
    downPaymentType: 'fixed',
    installmentCount: 1,
    installmentFrequency: 'mensal',
    latePaymentInterest: 0,
    earlyPaymentDiscount: 0,
    gracePeriodDays: 0,
    paymentConditions: '',
    collectionNotes: '',
    ...initialConfig
  }));

  const [userId, setUserId] = useState<string | null>(null);
  const [isCreatingAutoEntry, setIsCreatingAutoEntry] = useState(false);

  const [benefitValueInput, setBenefitValueInput] = useState('');
  const [initialFeeInput, setInitialFeeInput] = useState('');
  const [successFeeInput, setSuccessFeeInput] = useState('');
  const [installmentValueInput, setInstallmentValueInput] = useState('');
  const [undefinedInstallmentValueInput, setUndefinedInstallmentValueInput] = useState('');
  const [successFeeIndependentInput, setSuccessFeeIndependentInput] = useState('');
  const [successFeeIndependentPaidInput, setSuccessFeeIndependentPaidInput] = useState('');

  const calculateBenefitInstallments = () => {
    if (config.benefitPaymentType === 'installments' && config.benefitValue > 0 && config.benefitInstallmentCount > 0) {
      const installmentValue = config.benefitValue / config.benefitInstallmentCount;
      updateConfig({ benefitInstallmentValue: installmentValue });
    }
  };

  const calculatePaymentDates = (firstPaymentDate: string, frequency: string, count: number): string[] => {
    const dates: string[] = [];
    const startDate = new Date(firstPaymentDate);
    
    for (let i = 0; i < count; i++) {
      const installmentDate = new Date(startDate);
      
      switch (frequency) {
        case 'semanal':
          installmentDate.setDate(installmentDate.getDate() + (i * 7));
          break;
        case 'quinzenal':
          installmentDate.setDate(installmentDate.getDate() + (i * 15));
          break;
        case 'mensal':
        default:
          // Manter o mesmo dia do mês, apenas avançar os meses
          installmentDate.setMonth(installmentDate.getMonth() + i);
          break;
        case 'bimestral':
          installmentDate.setMonth(installmentDate.getMonth() + (i * 2));
          break;
        case 'trimestral':
          installmentDate.setMonth(installmentDate.getMonth() + (i * 3));
          break;
        case 'semestral':
          installmentDate.setMonth(installmentDate.getMonth() + (i * 6));
          break;
        case 'anual':
          installmentDate.setFullYear(installmentDate.getFullYear() + i);
          break;
      }
      
      dates.push(installmentDate.toISOString().split('T')[0]);
    }
    
    return dates;
  };

  // Obter usuário atual
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
      }
    };
    getCurrentUser();
  }, []);

  useEffect(() => {
    calculateBenefitInstallments();
  }, [config.benefitValue, config.benefitInstallmentCount, config.benefitPaymentType]);

  // Sincronizar automaticamente quando tipo de benefício for indefinido
  useEffect(() => {
    if (config.benefitValueType === 'undefined' && config.benefitValue > 0) {
      // Se o valor do benefício foi definido e é indefinido, sincronizar automaticamente
      if (config.undefinedBenefitFirstInstallmentValue === 0 || 
          config.undefinedBenefitFirstInstallmentValue !== config.benefitValue) {
        console.log('Sincronizando valor do benefício indefinido:', config.benefitValue);
        updateConfig({ 
          undefinedBenefitFirstInstallmentValue: config.benefitValue,
          undefinedBenefitFirstInstallmentsCount: 1
        });
      }
    }
  }, [config.benefitValueType, config.benefitValue, config.undefinedBenefitFirstInstallmentValue]);

  // Debug: monitorar mudanças no benefitValueType
  useEffect(() => {
    console.log('benefitValueType mudou para:', config.benefitValueType);
  }, [config.benefitValueType]);

  // Gerenciar lançamento automático de honorários iniciais
  const handleInitialFeeAutoEntry = async (futureDate: string, amount: number) => {
    if (!userId || !futureDate || amount <= 0) return;

    setIsCreatingAutoEntry(true);
    try {
      // Se já existe uma entrada automática, remove primeiro
      if (config.initialFeeAutoEntryId) {
        await removeAutoFinancialEntry(config.initialFeeAutoEntryId);
      }

      // Cria nova entrada automática
      const result = await createInitialFeeAutoEntry(
        {
          amount,
          futurePaymentDate: futureDate,
          description: `Honorários Iniciais - Recebimento Futuro (${formatCurrency(amount)})`
        },
        userId
      );

      if (result.success && result.entryId) {
        updateConfig({ initialFeeAutoEntryId: result.entryId });
        toast({
          title: "Lançamento automático criado",
          description: "A receita futura foi registrada no sistema financeiro.",
        });
      } else {
        throw new Error(result.error || 'Erro ao criar lançamento automático');
      }
    } catch (error) {
      console.error('Erro ao criar lançamento automático:', error);
      toast({
        title: "Erro",
        description: "Não foi possível criar o lançamento automático.",
        variant: "destructive",
      });
    } finally {
      setIsCreatingAutoEntry(false);
    }
  };

  // Remover lançamento automático
  const removeInitialFeeAutoEntry = async () => {
    if (!config.initialFeeAutoEntryId) return;

    try {
      const result = await removeAutoFinancialEntry(config.initialFeeAutoEntryId);
      if (result.success) {
        updateConfig({ initialFeeAutoEntryId: undefined });
        toast({
          title: "Lançamento removido",
          description: "A receita futura foi removida do sistema financeiro.",
        });
      }
    } catch (error) {
      console.error('Erro ao remover lançamento automático:', error);
    }
  };

  // DESABILITADO: Monitorar mudanças na data futura e valor dos honorários iniciais
  // Este useEffect estava criando entradas financeiras automaticamente ao editar valores
  // Agora as entradas só são criadas quando o processo é salvo
  /*
  useEffect(() => {
    if (config.initialFeePaymentStatus === 'pending' && 
        config.initialFeeFuturePaymentDate && 
        config.initialFeeAmount > 0 && 
        config.hasInitialFee) {
      handleInitialFeeAutoEntry(config.initialFeeFuturePaymentDate, config.initialFeeAmount);
    } else if (config.initialFeeAutoEntryId && 
               (!config.initialFeeFuturePaymentDate || 
                config.initialFeePaymentStatus === 'paid' || 
                !config.hasInitialFee)) {
      removeInitialFeeAutoEntry();
    }
  }, [config.initialFeeFuturePaymentDate, config.initialFeeAmount, config.initialFeePaymentStatus, config.hasInitialFee, userId]);
  */

  const updateConfig = (updates: Partial<FeeConfiguration>) => {
    console.log('updateConfig chamado com:', updates);
    const newConfig = { ...config, ...updates };
    console.log('Novo config:', newConfig);
    setConfig(newConfig);
    onConfigChange(newConfig);
  };

  const calculateEstimates = () => {
    const estimates = {
      initialFeeCalculated: 0,
      successFeeCalculated: 0,
      successFeeIndependentCalculated: 0,
      retroactiveFeeCalculated: 0,
      total: 0
    };

    // Se for Pro Bono, não calcular honorários
    if (config.isProBono) {
      return estimates;
    }

    // Calcular honorários iniciais
    if (config.hasInitialFee && config.benefitValue > 0) {
      if (config.initialFeeType === 'percentage') {
        estimates.initialFeeCalculated = (config.benefitValue * config.initialFeeAmount) / 100;
      } else {
        estimates.initialFeeCalculated = config.initialFeeAmount;
      }
    }

    // Calcular honorários de êxito independentes (prioridade)
    if (config.isSuccessFeeIndependent) {
      estimates.successFeeIndependentCalculated = config.successFeeIndependentValue;
      
      // Se parte já foi paga, subtrair do total
      if (config.successFeeIndependentAlreadyPaid) {
        estimates.successFeeIndependentCalculated -= config.successFeeIndependentPaidAmount;
      }
    } else if (config.hasSuccessFee || config.isBenefitUndefined) {
      // Calcular base de cálculo para honorários de êxito tradicionais
      let calculationBase = 0;
      
      if (config.isBenefitUndefined && config.undefinedBenefitFirstInstallmentValue > 0) {
        // BPC/LOAS: usar primeiras parcelas para cálculo
        calculationBase = config.undefinedBenefitFirstInstallmentValue * config.undefinedBenefitFirstInstallmentsCount;
        
        // Para benefício indefinido, usar valores padrão se não configurado
        const successFeeValue = config.successFeeValue || 0;
        const successFeeType = config.successFeeType || 'percentage_global';
        
        // Se for porcentagem por parcela, calcular diferente
        if (successFeeType === 'percentage_installment') {
          estimates.successFeeCalculated = (config.undefinedBenefitFirstInstallmentValue * successFeeValue / 100) * config.undefinedBenefitFirstInstallmentsCount;
        } else {
          estimates.successFeeCalculated = (calculationBase * successFeeValue) / 100;
        }
      } else if (config.benefitValue > 0) {
        // Benefício normal: usar valor total ou por parcela
        if (config.successFeeType === 'percentage_installment' && config.benefitPaymentType === 'installments' && config.benefitInstallmentValue > 0) {
          // Por parcela: calcular sobre cada parcela
          calculationBase = config.benefitInstallmentValue;
          estimates.successFeeCalculated = (calculationBase * config.successFeeValue / 100) * config.benefitInstallmentCount;
        } else {
          // Global: usar valor total do benefício
          calculationBase = config.benefitValue;
          estimates.successFeeCalculated = (calculationBase * config.successFeeValue) / 100;
        }
      }
      
      // Calcular honorários sobre retroativo se configurado
      if (config.hasRetroactivePayment && config.retroactivePaymentValue > 0) {
        if (config.retroactivePaymentType === 'percentage') {
          estimates.retroactiveFeeCalculated = (config.retroactivePaymentValue * config.successFeeValue) / 100;
        } else {
          estimates.retroactiveFeeCalculated = config.retroactivePaymentValue;
        }
      }
      
      if (config.initialFeeDeductFromFuture && config.hasInitialFee) {
        estimates.successFeeCalculated = Math.max(0, estimates.successFeeCalculated - estimates.initialFeeCalculated);
      }
    }

    estimates.total = estimates.initialFeeCalculated + estimates.successFeeCalculated + estimates.successFeeIndependentCalculated + estimates.retroactiveFeeCalculated;
    return estimates;
  };

  const estimates = calculateEstimates();

  return (
    <div className="space-y-6">
      {/* Valor do Benefício */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Valor do Benefício do Cliente
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Valor do Benefício</Label>
              <Input
                type="text"
                placeholder="Ex: 50000 ou 50.000,00"
                value={benefitValueInput || (config.benefitValue > 0 ? formatCurrency(config.benefitValue) : '')}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  setBenefitValueInput(inputValue);
                  const numericValue = parseCurrency(inputValue);
                  updateConfig({ benefitValue: numericValue });
                }}
                onBlur={() => {
                  if (config.benefitValue > 0) {
                    setBenefitValueInput('');
                  }
                }}
                className="text-lg font-medium"
              />
            </div>
            <div>
              <Label>Tipo do Valor</Label>
              <Select
                value={config.benefitValueType}
                onValueChange={(value: 'estimated' | 'confirmed' | 'undefined') => {
                  console.log('Tipo de valor alterado para:', value);
                  
                  // Atualizar tudo de uma vez para evitar conflitos
                  const updates: Partial<FeeConfiguration> = { benefitValueType: value };
                  
                  // Se for benefício indefinido, ativar automaticamente nos honorários de êxito
                  if (value === 'undefined') {
                    console.log('Ativando benefício indefinido');
                    updates.isBenefitUndefined = true;
                    updates.benefitPaymentType = 'installments'; // Indefinitivo = parcelado
                    updates.benefitInstallmentCount = 1; // Será definido pelos honorários
                    updates.benefitFirstPaymentDatePending = false; // Não pendente, precisa da data
                    
                    // Ativar honorários de êxito automaticamente para benefício indefinido
                    updates.hasSuccessFee = true;
                    updates.successFeeType = 'percentage_global';
                    updates.successFeeValue = 0; // Será definido pelo usuário
                    updates.successFeeTrigger = 'recebimento';
                    
                    // Sincronizar automaticamente o valor do benefício com o valor das parcelas
                    if (config.benefitValue > 0) {
                      updates.undefinedBenefitFirstInstallmentValue = config.benefitValue;
                      updates.undefinedBenefitFirstInstallmentsCount = 1;
                      console.log('Sincronizando valor automaticamente:', config.benefitValue);
                    }
                  } else {
                    // Se não for indefinido, desativar
                    updates.isBenefitUndefined = false;
                  }
                  
                  updateConfig(updates);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="estimated">Valor Previsto</SelectItem>
                  <SelectItem value="confirmed">Valor Definitivo</SelectItem>
                  <SelectItem value="undefined">Benefício Indefinido (BPC/LOAS, Aposentadoria, Pensão)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Configuração de Parcelamento do Benefício */}
          <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <Label className="text-sm font-medium text-blue-800">
              {config.benefitValueType === 'undefined' ? 'Tempo de Recebimento do Benefício' : 'Forma de Recebimento do Benefício'}
            </Label>
            
            {config.benefitValueType === 'undefined' ? (
              <div className="p-3 bg-orange-100 border border-orange-300 rounded text-sm text-orange-800">
                <strong>🔄 Indefinitivo/Vitalício:</strong> O benefício será recebido mensalmente de forma permanente. 
                Configure apenas a data do primeiro recebimento para cálculo dos honorários.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="lump_sum"
                    name="benefitPaymentType"
                    checked={config.benefitPaymentType === 'lump_sum'}
                    onChange={() => updateConfig({ benefitPaymentType: 'lump_sum' })}
                    className="text-blue-600"
                  />
                  <Label htmlFor="lump_sum" className="text-sm">À Vista</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="installments"
                    name="benefitPaymentType"
                    checked={config.benefitPaymentType === 'installments'}
                    onChange={() => updateConfig({ benefitPaymentType: 'installments' })}
                    className="text-blue-600"
                  />
                  <Label htmlFor="installments" className="text-sm">Parcelado</Label>
                </div>
              </div>
            )}
            
            {config.benefitPaymentType === 'installments' && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                {config.benefitValueType === 'undefined' ? (
                  // Para benefícios indefinidos, mostrar apenas data de recebimento
                  <div className="col-span-2">
                    <Label className="text-xs text-blue-700">Data do Primeiro Recebimento</Label>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="benefitDatePending"
                          checked={config.benefitFirstPaymentDatePending || false}
                          onChange={(e) => updateConfig({ 
                            benefitFirstPaymentDatePending: e.target.checked,
                            benefitFirstPaymentDate: e.target.checked ? '' : config.benefitFirstPaymentDate
                          })}
                          className="rounded border-gray-300"
                        />
                        <Label htmlFor="benefitDatePending" className="text-xs text-gray-600">
                          Data ainda não definida (Pendente)
                        </Label>
                      </div>
                      {!config.benefitFirstPaymentDatePending && (
                        <Input
                          type="date"
                          value={config.benefitFirstPaymentDate}
                          onChange={(e) => updateConfig({ benefitFirstPaymentDate: e.target.value })}
                          className="text-sm"
                          placeholder="Selecione a data"
                        />
                      )}
                      {config.benefitFirstPaymentDatePending && (
                        <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                          ⚠️ Data de recebimento pendente - será exibida nos alertas
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-blue-600 mt-1">
                      💡 Esta data será usada para calcular os honorários de êxito
                    </p>
                  </div>
                ) : (
                  // Para benefícios normais, mostrar configuração completa
                  <>
                    <div>
                      <Label className="text-xs text-blue-700">Número de Parcelas</Label>
                      <Input
                        type="number"
                        min="1"
                        max="60"
                        value={config.benefitInstallmentCount}
                        onChange={(e) => updateConfig({ benefitInstallmentCount: parseInt(e.target.value) || 1 })}
                        className="text-sm"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-xs text-blue-700">Frequência</Label>
                      <Select value={config.benefitInstallmentFrequency} onValueChange={(value: 'mensal' | 'quinzenal' | 'semanal') => updateConfig({ benefitInstallmentFrequency: value })}>
                        <SelectTrigger className="text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mensal">Mensal</SelectItem>
                          <SelectItem value="quinzenal">Quinzenal</SelectItem>
                          <SelectItem value="semanal">Semanal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-xs text-blue-700">Primeira Data de Recebimento</Label>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="benefitDatePending"
                            checked={config.benefitFirstPaymentDatePending || false}
                            onChange={(e) => updateConfig({ 
                              benefitFirstPaymentDatePending: e.target.checked,
                              benefitFirstPaymentDate: e.target.checked ? '' : config.benefitFirstPaymentDate
                            })}
                            className="rounded border-gray-300"
                          />
                          <Label htmlFor="benefitDatePending" className="text-xs text-gray-600">
                            Data ainda não definida (Pendente)
                          </Label>
                        </div>
                        {!config.benefitFirstPaymentDatePending && (
                          <Input
                            type="date"
                            value={config.benefitFirstPaymentDate}
                            onChange={(e) => updateConfig({ benefitFirstPaymentDate: e.target.value })}
                            className="text-sm"
                            placeholder="Selecione a data"
                          />
                        )}
                        {config.benefitFirstPaymentDatePending && (
                          <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                            ⚠️ Data de recebimento pendente - será exibida nos alertas
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-xs text-blue-700">Valor por Parcela</Label>
                      <Input
                        type="text"
                        placeholder="Ex: 5000 ou 5.000,00"
                        value={installmentValueInput || (config.benefitInstallmentValue > 0 ? formatCurrency(config.benefitInstallmentValue) : '')}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          setInstallmentValueInput(inputValue);
                          const numericValue = parseCurrency(inputValue);
                          updateConfig({ 
                            benefitInstallmentValue: numericValue,
                            benefitValue: numericValue * config.benefitInstallmentCount
                          });
                        }}
                        onBlur={() => {
                          if (config.benefitInstallmentValue > 0) {
                            setInstallmentValueInput('');
                          }
                        }}
                        className="text-sm"
                      />
                    </div>
                  </>
                )}
              </div>
            )}
            
            {config.benefitPaymentType === 'lump_sum' && (
              <div>
                <Label className="text-xs text-blue-700">Data de Recebimento</Label>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="benefitDatePendingLumpSum"
                      checked={config.benefitFirstPaymentDatePending || false}
                      onChange={(e) => updateConfig({ 
                        benefitFirstPaymentDatePending: e.target.checked,
                        benefitFirstPaymentDate: e.target.checked ? '' : config.benefitFirstPaymentDate
                      })}
                      className="rounded border-gray-300"
                    />
                    <Label htmlFor="benefitDatePendingLumpSum" className="text-xs text-gray-600">
                      Data ainda não definida (Pendente)
                    </Label>
                  </div>
                  {!config.benefitFirstPaymentDatePending && (
                    <Input
                      type="date"
                      value={config.benefitFirstPaymentDate}
                      onChange={(e) => updateConfig({ benefitFirstPaymentDate: e.target.value })}
                      className="text-sm"
                      placeholder="Selecione a data"
                    />
                  )}
                  {config.benefitFirstPaymentDatePending && (
                    <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                      ⚠️ Data de recebimento pendente - será exibida nos alertas
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Exibição das datas de recebimento calculadas */}
          {config.benefitPaymentType === 'installments' && config.benefitFirstPaymentDate && config.benefitInstallmentCount > 0 && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="text-sm font-medium text-blue-900 mb-3">📅 Cronograma de Recebimento</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
                {calculatePaymentDates(
                  config.benefitFirstPaymentDate,
                  config.benefitInstallmentFrequency,
                  config.benefitInstallmentCount
                ).map((date, index) => (
                  <div key={index} className="flex justify-between items-center p-2 bg-white rounded border">
                    <span className="font-medium text-gray-700">{index + 1}ª parcela:</span>
                    <span className="text-blue-600">{new Date(date).toLocaleDateString('pt-BR')}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-blue-200">
                <div className="flex justify-between items-center text-sm">
                  <span className="font-medium text-blue-900">Total do Benefício:</span>
                  <span className="font-bold text-blue-900">{formatCurrency(config.benefitValue)}</span>
                </div>
                <div className="flex justify-between items-center text-sm mt-1">
                  <span className="font-medium text-blue-900">Valor por Parcela:</span>
                  <span className="font-bold text-blue-900">{formatCurrency(config.benefitInstallmentValue)}</span>
                </div>
              </div>
            </div>
          )}

          <div>
            <Label>Observações sobre o Valor</Label>
            <Textarea
              value={config.benefitValueNotes}
              onChange={(e) => updateConfig({ benefitValueNotes: e.target.value })}
              placeholder="Ex: Valor estimado baseado em cálculos iniciais, sujeito a alteração após decisão..."
              rows={2}
            />
          </div>
          
          
          {config.benefitValueType === 'estimated' && (
            <Alert>
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>
                <strong>Valor Previsto:</strong> Os cálculos serão reajustados automaticamente quando o valor definitivo for informado.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Honorários Iniciais */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5" />
            Honorários Iniciais
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch
              checked={config.hasInitialFee}
              onCheckedChange={(checked) => updateConfig({ hasInitialFee: checked })}
            />
            <Label>Cobrar honorários iniciais</Label>
          </div>
          
          {config.hasInitialFee && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Status do Pagamento</Label>
                  <Select
                    value={config.initialFeePaymentStatus}
                    onValueChange={(value: 'pending' | 'paid') => updateConfig({ initialFeePaymentStatus: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pendente</SelectItem>
                      <SelectItem value="paid">Pago</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {config.initialFeePaymentStatus === 'paid' && (
                  <div>
                    <Label>Data do Pagamento</Label>
                    <Input
                      type="date"
                      value={config.initialFeePaymentDate}
                      onChange={(e) => updateConfig({ initialFeePaymentDate: e.target.value })}
                    />
                  </div>
                )}
                
                {config.initialFeePaymentStatus === 'pending' && (
                  <div>
                    <Label>Data Futura de Recebimento (Opcional)</Label>
                    <Input
                      type="date"
                      value={config.initialFeeFuturePaymentDate}
                      onChange={(e) => updateConfig({ initialFeeFuturePaymentDate: e.target.value })}
                      placeholder="Selecione uma data futura"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Se definida, será lançada automaticamente nas receitas e despesas
                    </p>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{config.initialFeeType === 'percentage' ? 'Porcentagem (%)' : 'Valor'}</Label>
                  <Input
                    type={config.initialFeeType === 'percentage' ? 'number' : 'text'}
                    step={config.initialFeeType === 'percentage' ? '0.01' : undefined}
                    value={config.initialFeeType === 'percentage' 
                      ? config.initialFeeAmount 
                      : (initialFeeInput || (config.initialFeeAmount > 0 ? formatCurrency(config.initialFeeAmount) : ''))
                    }
                    onChange={(e) => {
                      if (config.initialFeeType === 'percentage') {
                        updateConfig({ initialFeeAmount: parseFloat(e.target.value) || 0 });
                      } else {
                        const inputValue = e.target.value;
                        setInitialFeeInput(inputValue);
                        const numericValue = parseCurrency(inputValue);
                        updateConfig({ initialFeeAmount: numericValue });
                      }
                    }}
                    onBlur={() => {
                      if (config.initialFeeType !== 'percentage' && config.initialFeeAmount > 0) {
                        setInitialFeeInput('');
                      }
                    }}
                    placeholder={config.initialFeeType === 'percentage' ? '0,00%' : 'R$ 0,00'}
                  />
                  {config.initialFeeType === 'percentage' && config.benefitValue > 0 && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Valor calculado: {formatCurrency(estimates.initialFeeCalculated)}
                    </p>
                  )}
                </div>
                <div>
                  <Label>Tipo</Label>
                  <Select
                    value={config.initialFeeType}
                    onValueChange={(value: 'fixed' | 'percentage') => updateConfig({ initialFeeType: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Valor Fixo</SelectItem>
                      <SelectItem value="percentage">% do Benefício</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  checked={config.initialFeeDeductFromFuture}
                  onCheckedChange={(checked) => updateConfig({ initialFeeDeductFromFuture: checked })}
                />
                <Label className="text-sm">Abater dos honorários de êxito futuros</Label>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Honorários de Êxito */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Percent className="w-5 h-5" />
            Honorários de Êxito
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch
              checked={config.hasSuccessFee}
              onCheckedChange={(checked) => updateConfig({ hasSuccessFee: checked })}
            />
            <Label>Cobrar honorários de êxito</Label>
          </div>
          
          {config.hasSuccessFee && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo de Cobrança</Label>
                  <Select
                    value={config.successFeeType}
                    onValueChange={(value: any) => updateConfig({ successFeeType: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage_global">% do Total do Benefício</SelectItem>
                      <SelectItem value="percentage_installment">% de Cada Parcela</SelectItem>
                      <SelectItem value="fixed_global">Valor Fixo Global</SelectItem>
                      <SelectItem value="fixed_monthly">Valor Fixo Mensal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Pro Bono</Label>
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={config.isProBono}
                      onCheckedChange={(checked) => updateConfig({ isProBono: checked })}
                    />
                    <Label className="text-sm">Este caso é Pro Bono</Label>
                  </div>
                  {config.isProBono && (
                    <p className="text-xs text-blue-600 mt-1">
                      💡 Não será cobrado nada do cliente, apenas registrado para relatório
                    </p>
                  )}
                </div>
                
                <div>
                  <Label>{config.successFeeType.includes('percentage') ? 'Porcentagem (%)' : 'Valor'}</Label>
                  <Input
                    type={config.successFeeType.includes('percentage') ? 'number' : 'text'}
                    step={config.successFeeType.includes('percentage') ? '0.01' : undefined}
                    value={config.successFeeType.includes('percentage')
                      ? config.successFeeValue
                      : (successFeeInput || (config.successFeeValue > 0 ? formatCurrency(config.successFeeValue) : ''))
                    }
                    onChange={(e) => {
                      if (config.successFeeType.includes('percentage')) {
                        updateConfig({ successFeeValue: parseFloat(e.target.value) || 0 });
                      } else {
                        const inputValue = e.target.value;
                        setSuccessFeeInput(inputValue);
                        const numericValue = parseCurrency(inputValue);
                        updateConfig({ successFeeValue: numericValue });
                      }
                    }}
                    onBlur={() => {
                      if (!config.successFeeType.includes('percentage') && config.successFeeValue > 0) {
                        setSuccessFeeInput('');
                      }
                    }}
                    placeholder={config.successFeeType.includes('percentage') ? '0,00%' : 'Ex: 10000 ou 10.000,00'}
                  />
                  {config.successFeeType.includes('percentage') && (config.benefitValue > 0 || config.isBenefitUndefined) && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Valor calculado: {formatCurrency(estimates.successFeeCalculated)}
                      {config.initialFeeDeductFromFuture && config.hasInitialFee && (
                        <span className="text-orange-600"> (já descontado honorário inicial)</span>
                      )}
                    </p>
                  )}
                </div>
              </div>
              
              <div>
                <Label>Gatilho para Cobrança</Label>
                <Select
                  value={config.successFeeTrigger}
                  onValueChange={(value: 'decisao' | 'recebimento' | 'ambos') => updateConfig({ successFeeTrigger: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="decisao">Decisão Favorável</SelectItem>
                    <SelectItem value="recebimento">Recebimento do Benefício</SelectItem>
                    <SelectItem value="ambos">Ambos (Decisão + Recebimento)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Configuração para BPC/LOAS (integrada nos Honorários de Êxito) */}
              <div className="space-y-3 p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={config.isBenefitUndefined}
                    onCheckedChange={(checked) => {
                      updateConfig({ isBenefitUndefined: checked });
                      // Se ativando benefício indefinido, sincronizar com valor do benefício
                      if (checked && config.benefitValue > 0) {
                        updateConfig({ 
                          undefinedBenefitFirstInstallmentValue: config.benefitValue,
                          undefinedBenefitFirstInstallmentsCount: 1
                        });
                      }
                    }}
                  />
                  <Label className="text-sm font-medium text-orange-800">🎯 Benefício Indefinido (BPC/LOAS, Aposentadoria, Pensão)</Label>
                </div>
                
                {config.isBenefitUndefined && (
                  <>
                    <p className="text-xs text-orange-700">
                      Para benefícios como BPC/LOAS, aposentadoria e pensão, configure os valores das primeiras parcelas para cálculo dos honorários.
                    </p>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-orange-700">Número de Primeiras Parcelas</Label>
                        <Input
                          type="number"
                          min="1"
                          max="12"
                          value={config.undefinedBenefitFirstInstallmentsCount}
                          onChange={(e) => updateConfig({ undefinedBenefitFirstInstallmentsCount: parseInt(e.target.value) || 3 })}
                          className="text-sm"
                          placeholder="Ex: 3"
                        />
                        <p className="text-xs text-orange-600 mt-1">Quantas parcelas usar para calcular os honorários</p>
                      </div>
                      
                      <div>
                        <Label className="text-xs text-orange-700">Valor de Cada Parcela</Label>
                        <Input
                          type="text"
                          placeholder="Ex: 1512 ou 1.512,00"
                          value={undefinedInstallmentValueInput || (config.undefinedBenefitFirstInstallmentValue > 0 ? formatCurrency(config.undefinedBenefitFirstInstallmentValue) : '')}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            setUndefinedInstallmentValueInput(inputValue);
                            const numericValue = parseCurrency(inputValue);
                            updateConfig({ undefinedBenefitFirstInstallmentValue: numericValue });
                          }}
                          onBlur={() => {
                            if (config.undefinedBenefitFirstInstallmentValue > 0) {
                              setUndefinedInstallmentValueInput('');
                            }
                          }}
                          className="text-sm"
                        />
                        <p className="text-xs text-orange-600 mt-1">Valor que o cliente receberá por parcela</p>
                      </div>
                    </div>
                    
                    {/* Sincronização automática com valor do benefício */}
                    {config.benefitValue > 0 && config.undefinedBenefitFirstInstallmentValue !== config.benefitValue && (
                      <div className="p-3 bg-blue-100 border border-blue-300 rounded text-xs text-blue-800">
                        <strong>💡 Sugestão:</strong> O valor do benefício (R$ {formatCurrency(config.benefitValue)}) pode ser usado como valor da parcela. 
                        <button 
                          type="button"
                          onClick={() => updateConfig({ undefinedBenefitFirstInstallmentValue: config.benefitValue })}
                          className="ml-2 text-blue-600 underline hover:text-blue-800"
                        >
                          Usar este valor
                        </button>
                      </div>
                    )}
                    
                    <div className="p-3 bg-orange-100 border border-orange-300 rounded text-xs text-orange-800">
                      <strong>💡 Exemplo:</strong> BPC/LOAS - 3 parcelas de R$ 1.512,00 = R$ 4.536,00 total para cálculo dos honorários
                    </div>
                  </>
                )}
              </div>
              
              {/* Configuração de Retroativo */}
              <div className="space-y-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={config.hasRetroactivePayment}
                    onCheckedChange={(checked) => updateConfig({ hasRetroactivePayment: checked })}
                  />
                  <Label className="text-sm font-medium text-purple-800">💰 Valor Retroativo do Benefício</Label>
                </div>
                
                {config.hasRetroactivePayment && (
                  <>
                    <p className="text-xs text-purple-700">
                      Configure o valor retroativo que o cliente receberá e quando será pago.
                    </p>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-purple-700">Tipo do Retroativo</Label>
                        <Select
                          value={config.retroactivePaymentType}
                          onValueChange={(value: 'fixed' | 'percentage') => updateConfig({ retroactivePaymentType: value })}
                        >
                          <SelectTrigger className="text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fixed">Valor Fixo</SelectItem>
                            <SelectItem value="percentage">Porcentagem do Benefício</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label className="text-xs text-purple-700">
                          {config.retroactivePaymentType === 'percentage' ? 'Porcentagem (%)' : 'Valor do Retroativo'}
                        </Label>
                        <Input
                          type={config.retroactivePaymentType === 'percentage' ? 'number' : 'text'}
                          step={config.retroactivePaymentType === 'percentage' ? '0.01' : undefined}
                          value={config.retroactivePaymentType === 'percentage' 
                            ? config.retroactivePaymentValue 
                            : (config.retroactivePaymentValue > 0 ? formatCurrency(config.retroactivePaymentValue) : '')
                          }
                          onChange={(e) => {
                            if (config.retroactivePaymentType === 'percentage') {
                              updateConfig({ retroactivePaymentValue: parseFloat(e.target.value) || 0 });
                            } else {
                              const numericValue = parseCurrency(e.target.value);
                              updateConfig({ retroactivePaymentValue: numericValue });
                            }
                          }}
                          placeholder={config.retroactivePaymentType === 'percentage' ? '0,00%' : 'Ex: 10000 ou 10.000,00'}
                          className="text-sm"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-xs text-purple-700">Data Prevista do Retroativo</Label>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="retroactiveDatePending"
                              checked={config.retroactivePaymentDatePending || false}
                              onChange={(e) => updateConfig({ 
                                retroactivePaymentDatePending: e.target.checked,
                                retroactivePaymentDate: e.target.checked ? '' : config.retroactivePaymentDate
                              })}
                              className="rounded border-gray-300"
                            />
                            <Label htmlFor="retroactiveDatePending" className="text-xs text-gray-600">
                              Data ainda não definida (Pendente)
                            </Label>
                          </div>
                          {!config.retroactivePaymentDatePending && (
                            <Input
                              type="date"
                              value={config.retroactivePaymentDate}
                              onChange={(e) => updateConfig({ retroactivePaymentDate: e.target.value })}
                              className="text-sm"
                              placeholder="Selecione a data"
                            />
                          )}
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-xs text-purple-700">Cobrança sobre Retroativo</Label>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="chargeOnRetroactive"
                              checked={config.chargeOnRetroactive}
                              onChange={(e) => updateConfig({ 
                                chargeOnRetroactive: e.target.checked,
                                chargeOnBoth: e.target.checked && config.chargeOnInstallments
                              })}
                              className="rounded border-gray-300"
                            />
                            <Label htmlFor="chargeOnRetroactive" className="text-xs text-gray-600">
                              Cobrar sobre retroativo
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="chargeOnInstallments"
                              checked={config.chargeOnInstallments}
                              onChange={(e) => updateConfig({ 
                                chargeOnInstallments: e.target.checked,
                                chargeOnBoth: e.target.checked && config.chargeOnRetroactive
                              })}
                              className="rounded border-gray-300"
                            />
                            <Label htmlFor="chargeOnInstallments" className="text-xs text-gray-600">
                              Cobrar sobre parcelas
                            </Label>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-purple-100 border border-purple-300 rounded text-xs text-purple-800">
                      <strong>💡 Exemplo:</strong> Retroativo de R$ 15.000,00 + 3 parcelas de R$ 1.512,00 = R$ 19.536,00 total para cálculo dos honorários
                    </div>
                  </>
                )}
              </div>
              
              <div>
                <Label>Condições Específicas</Label>
                <Textarea
                  value={config.successFeeConditions}
                  onChange={(e) => updateConfig({ successFeeConditions: e.target.value })}
                  placeholder="Ex: Honorários devidos apenas após o efetivo recebimento de cada parcela..."
                  rows={2}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* NOVA SEÇÃO: Honorários de Serviços */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5" />
            Honorários de Serviços
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch
              checked={config.isSuccessFeeIndependent}
              onCheckedChange={(checked) => updateConfig({ isSuccessFeeIndependent: checked })}
            />
            <Label>Honorários de serviços (sem benefício para o cliente)</Label>
          </div>
          
          {config.isSuccessFeeIndependent && (
            <>
              <Alert>
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>
                  <strong>💡 Honorários de Serviços:</strong> Configure um valor fixo para os honorários quando não há benefício para o cliente. 
                  Ideal para serviços como recursos de trânsito, recursos administrativos, consultorias, 
                  pareceres e outros trabalhos jurídicos onde apenas o serviço será prestado.
                </AlertDescription>
              </Alert>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Valor Total dos Honorários</Label>
                  <Input
                    type="text"
                    placeholder="Ex: 5000 ou 5.000,00"
                    value={successFeeIndependentInput || (config.successFeeIndependentValue > 0 ? formatCurrency(config.successFeeIndependentValue) : '')}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      setSuccessFeeIndependentInput(inputValue);
                      const numericValue = parseCurrency(inputValue);
                      updateConfig({ successFeeIndependentValue: numericValue });
                    }}
                    onBlur={() => {
                      if (config.successFeeIndependentValue > 0) {
                        setSuccessFeeIndependentInput('');
                      }
                    }}
                    className="text-lg font-medium"
                  />
                </div>
                
                <div>
                  <Label>Número de Parcelas</Label>
                  <Input
                    type="number"
                    min="1"
                    max="60"
                    value={config.successFeeIndependentInstallmentCount}
                    onChange={(e) => updateConfig({ successFeeIndependentInstallmentCount: parseInt(e.target.value) || 1 })}
                    className="text-lg"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Frequência das Parcelas</Label>
                  <Select 
                    value={config.successFeeIndependentInstallmentFrequency} 
                    onValueChange={(value: 'mensal' | 'bimestral' | 'trimestral' | 'semestral' | 'anual') => 
                      updateConfig({ successFeeIndependentInstallmentFrequency: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mensal">Mensal</SelectItem>
                      <SelectItem value="bimestral">Bimestral</SelectItem>
                      <SelectItem value="trimestral">Trimestral</SelectItem>
                      <SelectItem value="semestral">Semestral</SelectItem>
                      <SelectItem value="anual">Anual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Data do Primeiro Pagamento</Label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="successFeeIndependentDatePending"
                        checked={config.successFeeIndependentFirstPaymentDatePending || false}
                        onChange={(e) => updateConfig({ 
                          successFeeIndependentFirstPaymentDatePending: e.target.checked,
                          successFeeIndependentFirstPaymentDate: e.target.checked ? '' : config.successFeeIndependentFirstPaymentDate
                        })}
                        className="rounded border-gray-300"
                      />
                      <Label htmlFor="successFeeIndependentDatePending" className="text-xs text-gray-600">
                        Data ainda não definida (Pendente)
                      </Label>
                    </div>
                    {!config.successFeeIndependentFirstPaymentDatePending && (
                      <Input
                        type="date"
                        value={config.successFeeIndependentFirstPaymentDate}
                        onChange={(e) => updateConfig({ successFeeIndependentFirstPaymentDate: e.target.value })}
                        className="text-sm"
                        placeholder="Selecione a data"
                      />
                    )}
                    {config.successFeeIndependentFirstPaymentDatePending && (
                      <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                        ⚠️ Data de pagamento pendente - será exibida nos alertas
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Controle de Pagamentos Já Realizados */}
              <div className="space-y-3 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={config.successFeeIndependentAlreadyPaid}
                    onCheckedChange={(checked) => updateConfig({ successFeeIndependentAlreadyPaid: checked })}
                  />
                  <Label className="text-sm font-medium text-green-800">💰 Parte dos honorários já foi paga</Label>
                </div>
                
                {config.successFeeIndependentAlreadyPaid && (
                  <>
                    <p className="text-xs text-green-700">
                      Configure o valor já pago e a data do pagamento para controle adequado dos lançamentos financeiros.
                    </p>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-green-700">Valor Já Pago</Label>
                        <Input
                          type="text"
                          placeholder="Ex: 2000 ou 2.000,00"
                          value={successFeeIndependentPaidInput || (config.successFeeIndependentPaidAmount > 0 ? formatCurrency(config.successFeeIndependentPaidAmount) : '')}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            setSuccessFeeIndependentPaidInput(inputValue);
                            const numericValue = parseCurrency(inputValue);
                            updateConfig({ successFeeIndependentPaidAmount: numericValue });
                          }}
                          onBlur={() => {
                            if (config.successFeeIndependentPaidAmount > 0) {
                              setSuccessFeeIndependentPaidInput('');
                            }
                          }}
                          className="text-sm"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-xs text-green-700">Data do Pagamento</Label>
                        <Input
                          type="date"
                          value={config.successFeeIndependentPaidDate}
                          onChange={(e) => updateConfig({ successFeeIndependentPaidDate: e.target.value })}
                          className="text-sm"
                        />
                      </div>
                    </div>
                    
                    <div className="p-3 bg-green-100 border border-green-300 rounded text-xs text-green-800">
                      <strong>💡 Resumo:</strong> 
                      Valor total: {formatCurrency(config.successFeeIndependentValue)} | 
                      Já pago: {formatCurrency(config.successFeeIndependentPaidAmount)} | 
                      Restante: {formatCurrency(config.successFeeIndependentValue - config.successFeeIndependentPaidAmount)}
                    </div>
                  </>
                )}
              </div>
              
              {/* Cronograma de Pagamentos */}
              {config.successFeeIndependentValue > 0 && config.successFeeIndependentInstallmentCount > 0 && config.successFeeIndependentFirstPaymentDate && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-900 mb-3">📅 Cronograma de Pagamentos</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
                    {calculatePaymentDates(
                      config.successFeeIndependentFirstPaymentDate,
                      config.successFeeIndependentInstallmentFrequency,
                      config.successFeeIndependentInstallmentCount
                    ).map((date, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-white rounded border">
                        <span className="font-medium text-gray-700">{index + 1}ª parcela:</span>
                        <span className="text-blue-600">{new Date(date).toLocaleDateString('pt-BR')}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-medium text-blue-900">Valor por Parcela:</span>
                      <span className="font-bold text-blue-900">
                        {formatCurrency(config.successFeeIndependentValue / config.successFeeIndependentInstallmentCount)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Resumo dos Cálculos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5" />
            Resumo dos Cálculos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(config.benefitValue > 0 || config.isSuccessFeeIndependent) ? (
            <div className="space-y-6">
              {/* Informações do Benefício */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{formatCurrency(config.benefitValue)}</div>
                  <div className="text-sm text-gray-600">
                    {config.benefitValueType === 'undefined' ? 'Valor Mensal do Benefício' : 'Valor Total do Benefício'}
                  </div>
                  <Badge variant={
                    config.benefitValueType === 'confirmed' ? 'default' : 
                    config.benefitValueType === 'undefined' ? 'destructive' : 'secondary'
                  } className="mt-1">
                    {config.benefitValueType === 'confirmed' ? 'Valor Definitivo' : 
                     config.benefitValueType === 'undefined' ? 'Vitalício/Indefinido' : 'Valor Previsto'}
                  </Badge>
                </div>
                
                {config.benefitPaymentType === 'installments' && config.benefitInstallmentValue > 0 && (
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{formatCurrency(config.benefitInstallmentValue)}</div>
                    <div className="text-sm text-gray-600">Valor por Parcela</div>
                    <Badge variant="outline" className="mt-1">
                      {config.benefitInstallmentCount}x {config.benefitInstallmentFrequency}
                    </Badge>
                  </div>
                )}
              </div>
              
              {/* Detalhamento dos Honorários */}
              {(config.hasInitialFee || config.hasSuccessFee || config.isBenefitUndefined || config.isSuccessFeeIndependent) && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Detalhamento dos Honorários
                  </h4>
                  
                  <div className="grid grid-cols-1 gap-3">
                    {config.hasInitialFee && (
                      <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-700">Honorários Iniciais</span>
                          <span className="font-bold text-yellow-700">{formatCurrency(estimates.initialFeeCalculated)}</span>
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          {config.initialFeeType === 'percentage' 
                            ? `${config.initialFeeAmount}% sobre ${formatCurrency(config.initialFeeBaseValue || config.benefitValue)}`
                            : 'Valor fixo'
                          }
                        </div>
                        <Badge 
                          variant={config.initialFeePaymentStatus === 'paid' ? 'default' : 'destructive'} 
                          className="mt-1 text-xs"
                        >
                          {config.initialFeePaymentStatus === 'paid' ? 'Pago' : 'Pendente'}
                        </Badge>
                      </div>
                    )}
                    
                    {/* NOVA SEÇÃO: Honorários de Serviços */}
                    {config.isSuccessFeeIndependent && !config.isProBono && (
                      <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-700">Honorários de Serviços</span>
                          <span className="font-bold text-orange-700">{formatCurrency(estimates.successFeeIndependentCalculated)}</span>
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Valor fixo para serviços (sem benefício): {formatCurrency(config.successFeeIndependentValue)}
                          {config.successFeeIndependentAlreadyPaid && (
                            <span className="text-green-600">
                              {' '}| Já pago: {formatCurrency(config.successFeeIndependentPaidAmount)}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Parcelas: {config.successFeeIndependentInstallmentCount}x {config.successFeeIndependentInstallmentFrequency}
                        </div>
                        {config.successFeeIndependentFirstPaymentDate && (
                          <div className="text-xs text-gray-500 mt-1">
                            Primeiro pagamento: {new Date(config.successFeeIndependentFirstPaymentDate).toLocaleDateString('pt-BR')}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {(config.hasSuccessFee || config.isBenefitUndefined) && !config.isProBono && !config.isSuccessFeeIndependent && (
                      <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-700">Honorários de Êxito</span>
                          <span className="font-bold text-green-700">{formatCurrency(estimates.successFeeCalculated)}</span>
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          {config.isBenefitUndefined
                            ? `${config.successFeeValue || 0}% sobre ${config.undefinedBenefitFirstInstallmentsCount} parcelas (${formatCurrency(config.undefinedBenefitFirstInstallmentValue)} cada) - Benefício Vitalício`
                            : config.successFeeType.includes('percentage') 
                              ? (
                                  config.successFeeType === 'percentage_installment'
                                    ? `${config.successFeeValue}% por parcela (${formatCurrency(config.benefitInstallmentValue * config.successFeeValue / 100)} × ${config.benefitInstallmentCount} parcelas)`
                                    : `${config.successFeeValue}% sobre o total`
                                )
                              : config.successFeeType.includes('monthly') 
                                ? `${formatCurrency(config.successFeeValue)} por parcela`
                                : `Valor fixo total`
                          }
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Gatilho: {config.successFeeTrigger === 'decisao' ? 'Decisão Favorável' : 
                                   config.successFeeTrigger === 'recebimento' ? 'Recebimento' : 'Decisão + Recebimento'}
                        </div>
                      </div>
                    )}
                    
                    {config.hasRetroactivePayment && config.retroactivePaymentValue > 0 && !config.isProBono && (
                      <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-700">Honorários sobre Retroativo</span>
                          <span className="font-bold text-purple-700">{formatCurrency(estimates.retroactiveFeeCalculated)}</span>
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          {config.retroactivePaymentType === 'percentage' 
                            ? `${config.successFeeValue}% sobre retroativo de ${formatCurrency(config.retroactivePaymentValue)}`
                            : `Valor fixo de ${formatCurrency(config.retroactivePaymentValue)}`
                          }
                        </div>
                      </div>
                    )}
                    
                    {config.isProBono && (
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-700">Pro Bono</span>
                          <span className="font-bold text-blue-700">R$ 0,00</span>
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Caso Pro Bono - Não há cobrança de honorários
                        </div>
                        <div className="text-xs text-blue-600 mt-1">
                          💡 Será registrado apenas para relatório de impacto social
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Total dos Honorários */}
                  <div className="p-4 bg-gray-100 rounded-lg border-2 border-gray-300">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-gray-800">Total dos Honorários</span>
                      <span className="text-xl font-bold text-gray-900">{formatCurrency(estimates.total)}</span>
                    </div>
                    <div className="flex justify-between items-center mt-2 text-sm">
                      <span className="text-gray-600">Percentual sobre o benefício</span>
                      <span className="font-medium text-gray-700">
                        {((estimates.total / config.benefitValue) * 100).toFixed(2)}%
                      </span>
                    </div>
                    
                    {config.benefitPaymentType === 'installments' && config.benefitInstallmentValue > 0 && (
                      <div className="flex justify-between items-center mt-1 text-sm">
                        <span className="text-gray-600">Honorários por parcela (aprox.)</span>
                        <span className="font-medium text-gray-700">
                          {formatCurrency(estimates.total / config.benefitInstallmentCount)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Explicação da Lógica de Cálculo */}
              {(config.benefitPaymentType === 'installments' || config.isBenefitUndefined) && (
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200 mb-4">
                  <h4 className="font-medium text-yellow-800 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    💡 Como funciona o cálculo dos honorários
                  </h4>
                  <div className="text-sm text-yellow-700 space-y-2">
                    {config.isBenefitUndefined ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="font-medium mb-1">📊 Cálculo para Benefício Vitalício:</p>
                          <ul className="space-y-1 text-xs">
                            <li>• Valor mensal do benefício: <strong>{formatCurrency(config.undefinedBenefitFirstInstallmentValue)}</strong></li>
                            <li>• Honorários de êxito ({config.successFeeValue || 0}%): <strong>{formatCurrency((config.undefinedBenefitFirstInstallmentValue * (config.successFeeValue || 0)) / 100)}</strong></li>
                            <li>• Cliente recebe mensalmente: <strong>{formatCurrency(config.undefinedBenefitFirstInstallmentValue - ((config.undefinedBenefitFirstInstallmentValue * (config.successFeeValue || 0)) / 100))}</strong></li>
                            <li>• Base para cálculo: <strong>{config.undefinedBenefitFirstInstallmentsCount} parcelas de {formatCurrency(config.undefinedBenefitFirstInstallmentValue)}</strong></li>
                          </ul>
                        </div>
                        <div>
                          <p className="font-medium mb-1">🧮 Resumo do Benefício:</p>
                          <ul className="space-y-1 text-xs">
                            <li>• Total base para honorários: <strong>{formatCurrency(config.undefinedBenefitFirstInstallmentValue * config.undefinedBenefitFirstInstallmentsCount)}</strong></li>
                            <li>• Honorários totais calculados: <strong>{formatCurrency(estimates.successFeeCalculated)}</strong></li>
                            <li className="text-green-700">• <strong>Cliente não perde valor líquido mensal</strong></li>
                            <li className="text-blue-700">• <strong>Honorários cobrados apenas sobre as primeiras parcelas</strong></li>
                          </ul>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="font-medium mb-1">📊 Cálculo por parcela:</p>
                          <ul className="space-y-1 text-xs">
                            <li>• Valor bruto da parcela: <strong>{formatCurrency(config.benefitInstallmentValue)}</strong></li>
                            <li>• Honorários de êxito ({config.successFeeValue}%): <strong>{formatCurrency(config.benefitInstallmentValue * config.successFeeValue / 100)}</strong></li>
                            <li>• Valor líquido da parcela: <strong>{formatCurrency(config.benefitInstallmentValue - (config.benefitInstallmentValue * config.successFeeValue / 100))}</strong></li>
                          </ul>
                        </div>
                        <div>
                           <p className="font-medium mb-1">🧮 Cálculo total:</p>
                           <ul className="space-y-1 text-xs">
                             <li>• Total líquido ({config.benefitInstallmentCount} parcelas): <strong>{formatCurrency((config.benefitInstallmentValue - (config.benefitInstallmentValue * config.successFeeValue / 100)) * config.benefitInstallmentCount)}</strong></li>
                             <li className="text-green-700">• <strong>Sem honorários iniciais: {formatCurrency((config.benefitInstallmentValue - (config.benefitInstallmentValue * config.successFeeValue / 100)) * config.benefitInstallmentCount)}</strong></li>
                             {config.hasInitialFee && estimates.initialFeeCalculated > 0 && (
                               <>
                                 <li>• Menos honorários iniciais: <strong>-{formatCurrency(estimates.initialFeeCalculated)}</strong></li>
                                 <li className="border-t border-yellow-300 pt-1 mt-2 text-blue-700">• <strong>Com honorários iniciais: {formatCurrency(config.benefitValue - estimates.total)}</strong></li>
                               </>
                             )}
                           </ul>
                         </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Resumo Final */}
              <div className="p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-gray-800 mb-3">Resumo Final do Recebimento</h4>
                <div className="space-y-4">
                  {/* Cenário sem honorários iniciais */}
                  <div className="p-3 bg-green-100 rounded-lg border border-green-300">
                    <h5 className="text-sm font-medium text-green-800 mb-2">
                      {config.benefitValueType === 'undefined' ? '💰 Benefício Vitalício - Sem perda líquida' : '💰 Sem considerar honorários iniciais'}
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-green-600">
                          {config.benefitValueType === 'undefined' ? 'Cliente recebe mensalmente' : 'Valor líquido para o cliente'}
                        </div>
                        <div className="text-lg font-bold text-green-700">
                          {config.benefitValueType === 'undefined' 
                            ? formatCurrency(config.benefitValue)
                            : formatCurrency(config.benefitValue - (estimates.total - estimates.initialFeeCalculated))
                          }
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-green-600">
                          {config.benefitValueType === 'undefined' ? 'Honorários por parcela' : 'Apenas honorários de êxito'}
                        </div>
                        <div className="text-lg font-bold text-green-700">
                          {config.benefitValueType === 'undefined'
                            ? formatCurrency(estimates.successFeeCalculated)
                            : formatCurrency(estimates.total - estimates.initialFeeCalculated)
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Cenário com todos os honorários */}
                  {config.hasInitialFee && estimates.initialFeeCalculated > 0 && (
                    <div className="p-3 bg-blue-100 rounded-lg border border-blue-300">
                      <h5 className="text-sm font-medium text-blue-800 mb-2">🏛️ Considerando todos os honorários</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-blue-600">Valor líquido final</div>
                          <div className="text-lg font-bold text-blue-700">
                            {formatCurrency(config.benefitValue - estimates.total)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-blue-600">Total de honorários</div>
                          <div className="text-lg font-bold text-blue-700">
                            {formatCurrency(estimates.total)}
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-blue-600">
                        Inclui: Honorários iniciais ({formatCurrency(estimates.initialFeeCalculated)}) + Honorários de êxito ({formatCurrency(estimates.total - estimates.initialFeeCalculated)})
                      </div>
                    </div>
                  )}
                </div>
                
                {(config.benefitPaymentType === 'installments' && config.benefitInstallmentValue > 0) || config.isBenefitUndefined ? (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="text-sm text-gray-600">
                      {config.isBenefitUndefined ? 'Recebimento mensal (Benefício Vitalício)' : 'Recebimento por parcela'}
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Cliente recebe:</span>
                      <span className="font-bold text-green-600">
                        {(() => {
                          if (config.isBenefitUndefined) {
                            // Para benefício indefinido, cliente sempre recebe o valor mensal completo
                            return formatCurrency(config.undefinedBenefitFirstInstallmentValue);
                          } else {
                            // Calcular honorários por parcela baseado na configuração
                            let feesPerInstallment = 0;
                            
                            if (config.hasSuccessFee && config.successFeeCalculationBase === 'per_installment') {
                              // Se a base é por parcela, calcular sobre o valor da parcela
                              feesPerInstallment = config.benefitInstallmentValue * config.successFeeValue / 100;
                            } else {
                              // Se a base é total, dividir o total pelos número de parcelas
                              feesPerInstallment = estimates.total / config.benefitInstallmentCount;
                            }
                            
                            return formatCurrency(config.benefitInstallmentValue - feesPerInstallment);
                          }
                        })()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">
                        {config.isBenefitUndefined ? 'Honorários por mês:' : 'Honorários por parcela:'}
                      </span>
                      <span className="font-bold text-blue-600">
                        {(() => {
                          if (config.isBenefitUndefined) {
                            // Para benefício indefinido, honorários são calculados sobre as primeiras parcelas
                            return formatCurrency((config.undefinedBenefitFirstInstallmentValue * (config.successFeeValue || 0)) / 100);
                          } else {
                            if (config.hasSuccessFee && config.successFeeCalculationBase === 'per_installment') {
                              return formatCurrency(config.benefitInstallmentValue * config.successFeeValue / 100);
                            } else {
                              return formatCurrency(estimates.total / config.benefitInstallmentCount);
                            }
                          }
                        })()}
                      </span>
                    </div>
                    {config.isBenefitUndefined && (
                      <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                        <div className="text-xs text-blue-700">
                          <strong>💡 Benefício Vitalício:</strong> O cliente recebe o valor mensal completo sem desconto. 
                          Os honorários são calculados apenas sobre as primeiras {config.undefinedBenefitFirstInstallmentsCount} parcelas 
                          para fins de cobrança, mas não afetam o recebimento mensal do cliente.
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <Alert>
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>
                <strong>Atenção:</strong> Informe o valor do benefício ou configure honorários de serviços para visualizar os cálculos automáticos dos honorários.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Botões de Ação */}
      {onSave && (
        <div className="flex justify-end space-x-2">
          <Button onClick={() => onSave(config)} className="flex items-center gap-2">
            <Calculator className="w-4 h-4" />
            Salvar Configuração
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdvancedFeeConfiguration;